import { initializeApp, type FirebaseApp } from 'firebase/app';
import { enableNetwork, initializeFirestore, connectFirestoreEmulator, type Firestore } from 'firebase/firestore';
import { getAuth, signInWithCustomToken, type Auth, type UserCredential } from 'firebase/auth';
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions';

import { ENVIRONMENT } from '@/constants';

let app: FirebaseApp | null = null;
let firestore: Firestore | null = null;
let functions: Functions | null = null;

export function getFirebaseApp(): FirebaseApp {
  if (app) return app;

  const firebaseConfig = {
    apiKey: ENVIRONMENT.VITE_FIREBASE_API_KEY,
    authDomain: ENVIRONMENT.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: ENVIRONMENT.VITE_FIREBASE_PROJECT_ID,
    storageBucket: ENVIRONMENT.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: ENVIRONMENT.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: ENVIRONMENT.VITE_FIREBASE_APP_ID,
  };

  app = initializeApp(firebaseConfig);
  return app;
}

export function getFirebaseAuth(): Auth {
  return getAuth(getFirebaseApp());
}

export function getFirestore(): Firestore {
  if (firestore) return firestore;

  const app = getFirebaseApp();
  // Memory cache on purpose: the admin collections are tiny, and a persistent IndexedDB cache made Safari
  // stall for many seconds once its stored state aged (DM-1486).
  firestore = initializeFirestore(app, { ignoreUndefinedProperties: true });

  if (ENVIRONMENT.VITE_FIRESTORE_EMULATOR_HOST && ENVIRONMENT.VITE_FIRESTORE_EMULATOR_PORT) {
    connectFirestoreEmulator(firestore, ENVIRONMENT.VITE_FIRESTORE_EMULATOR_HOST, ENVIRONMENT.VITE_FIRESTORE_EMULATOR_PORT);
  }

  return firestore;
}

// Overlaps client setup with the auth round trips; no server stream opens until a listener exists.
export function warmUpFirebase(): void {
  getFirebaseAuth();
  enableNetwork(getFirestore()).catch((error) => console.warn('Firestore warm-up failed:', error));
}

export async function authenticateWithToken(customToken: string): Promise<UserCredential> {
  return signInWithCustomToken(getFirebaseAuth(), customToken);
}

export function getFirebaseFunctions(): Functions {
  if (functions) return functions;

  const app = getFirebaseApp();
  functions = getFunctions(app);

  if (ENVIRONMENT.VITE_FUNCTIONS_EMULATOR_HOST && ENVIRONMENT.VITE_FUNCTIONS_EMULATOR_PORT) {
    connectFunctionsEmulator(
      functions,
      ENVIRONMENT.VITE_FUNCTIONS_EMULATOR_HOST,
      Number(ENVIRONMENT.VITE_FUNCTIONS_EMULATOR_PORT)
    );
  }

  return functions;
}
